import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import useReveal from "@/hooks/useReveal";
import { PROJECT_FILTERS, STATUS_FILTERS } from "@/lib/constants";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("");
  const ref = useReveal();

  useEffect(() => {
    api.get("/projects/public").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const visible = useMemo(() => {
    let list = projects;
    if (filter !== "All") {
      list = list.filter(
        (p) => (p.services || []).includes(filter) || p.project_type === filter
      );
    }
    if (statusFilter) list = list.filter((p) => p.status === statusFilter);
    return list;
  }, [projects, filter, statusFilter]);

  return (
    <div ref={ref} className="pt-44 lg:pt-56" data-testid="projects-page">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 md:col-span-4"><div className="eyebrow">Portfolio · {projects.length} projects</div></div>
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Our projects.
            </h1>
            <p className="mt-6 text-lg text-stone max-w-2xl">
              A cross-section of residential, commercial and interior work — from early concepts and photoreal renders through built architecture.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {PROJECT_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
                filter === f ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-16">
          <span className="font-mono text-xs text-stone uppercase tracking-widest mr-2">Status:</span>
          <button
            data-testid="status-filter-all"
            onClick={() => setStatusFilter("")}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
              statusFilter === "" ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink"
            }`}
          >
            All
          </button>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setStatusFilter(s)}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                statusFilter === s ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Grid */}
        {visible.length === 0 ? (
          <div className="text-stone py-24 border-t border-line font-mono text-sm" data-testid="projects-empty">
            No projects match this filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 pb-32">
            {visible.map((p) => (
              <Link
                to={`/projects/${p.id}`}
                key={p.id}
                data-testid={`project-card-${p.id}`}
                className="reveal group block"
              >
                <div className="zoom-wrap bg-sand aspect-[4/5]">
                  <img src={mediaUrl(p.cover_image_url)} alt={p.name} className="zoom-img w-full h-full object-cover" />
                </div>
                <div className="mt-4 flex justify-between items-start gap-4">
                  <div>
                    <h3 className="font-heading text-xl lg:text-2xl tracking-tight">{p.name}</h3>
                    <div className="text-sm text-stone mt-1">{p.location} · {p.year}</div>
                    <div className="mt-2 text-xs font-mono uppercase tracking-widest text-stone">
                      {p.project_type} · {p.status}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
